import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { appSettings } from 'settings/app-settings'
import { PERS_AREA_ADMIN_TOKEN, restoreState } from 'utils'

import {
  ChangeVideoOrderBody,
  AddMDXNoteModel,
  FileOrDirectory,
  GetVideoByIdRes,
  Video,
  VideosArgs,
  VideosResponse,
  UpdateMainMDXNoteModel,
  UpdatePreviewMDXNoteModel,
  MDXParsedResponseDto,
  NoteType,
} from '../service'

export const videosService = createApi({
  baseQuery: fetchBaseQuery({
    baseUrl: appSettings.api.REACT_APP_VIDEO_URL,
    prepareHeaders: headers => {
      const token = restoreState(PERS_AREA_ADMIN_TOKEN, '')

      headers.set('token', token)

      return headers
    },
  }),
  endpoints: build => ({
    addDaysForVideoSettings: build.mutation({
      invalidatesTags: ['videos'],
      query: (settingIds: string[]) => {
        return {
          body: { settingIds },
          method: 'PUT',
          url: 'admin/videos/add-days',
        }
      },
    }),
    addVideo: build.mutation({
      invalidatesTags: ['videos'],
      query: (video: Video) => {
        return {
          body: video,
          method: 'POST',
          url: 'admin/videos',
        }
      },
    }),
    changeVideosOrder: build.mutation({
      invalidatesTags: ['videos'],
      query: (body: ChangeVideoOrderBody) => {
        return {
          body,
          method: 'POST',
          url: 'admin/videos/order',
        }
      },
    }),
    deleteVideo: build.mutation({
      invalidatesTags: ['videos'],
      query: (videoId: string) => {
        return {
          method: 'DELETE',
          url: `admin/videos/${videoId}`,
        }
      },
    }),
    getVideoById: build.query<GetVideoByIdRes, { id: string }>({
      providesTags: () => ['videos', 'delete comment-card'],

      query: ({ id }) => {
        return {
          url: `admin/videos/${id}`,
        }
      },
    }),
    getVideos: build.query<VideosResponse, VideosArgs>({
      providesTags: () => ['videos'],
      query: ({ course, page, pageSize, playlist, search, sortByReaction }) => {
        return {
          params: {
            course,
            page,
            pageSize,
            playlist,
            search,
            sortByReaction,
          },
          url: 'admin/videos',
        }
      },
    }),
    updateVideo: build.mutation({
      invalidatesTags: ['videos'],
      query: ({ video, videoId }) => {
        return {
          body: video,
          method: 'PUT',
          url: `admin/videos/${videoId}`,
        }
      },
    }),
    // Downloads thumbnails from the hosting (e.g. YouTube), uploads them to internal storage, removes previous images from storage, and replaces VideoImage records for the given video. Returns 204 on success.
    updateVideoImages: build.mutation({
      invalidatesTags: ['videos'],
      query: ({ videoId }: { videoId: string }) => {
        return {
          method: 'PUT',
          url: `admin/videos/${videoId}/images`,
        }
      },
    }),
    getMDXContent: build.query<MDXParsedResponseDto, { path: string }>({
      query: arg => {
        return {
          url: `admin/mdx-files/${arg.path}`,
        }
      },
    }),
    getMDXFilesTree: build.query<FileOrDirectory[], void>({
      query: () => {
        return {
          url: 'admin/mdx-files/tree',
        }
      },
    }),
    /* Добавление конспекта (main или preview) */
    addMDXNote: build.mutation({
      query: ({ videoId, body }: { videoId: string; body: AddMDXNoteModel }) => {
        return {
          body,
          method: 'POST',
          url: `admin/videos/${videoId}/mdx-notes`,
        }
      },
      invalidatesTags: ['videos'],
    }),
    /* Обновление конспекта (main) */
    updateMainMDXNote: build.mutation({
      query: ({ videoId, body }: { videoId: string; body: UpdateMainMDXNoteModel }) => {
        return {
          body,
          method: 'PUT',
          url: `admin/videos/${videoId}/mdx-notes/main`,
        }
      },
      invalidatesTags: ['videos'],
    }),
    /* Обновление конспекта (preview) */
    updatePreviewMDXNote: build.mutation({
      query: ({ videoId, body }: { videoId: string; body: UpdatePreviewMDXNoteModel }) => {
        return {
          body,
          method: 'PUT',
          url: `admin/videos/${videoId}/mdx-notes/preview`,
        }
      },
      invalidatesTags: ['videos'],
    }),
    deleteMDXNote: build.mutation({
      query: ({ videoId, noteType }: { videoId: string; noteType: NoteType }) => {
        return {
          method: 'DELETE',
          url: `admin/videos/${videoId}/mdx-notes`,
          params: {
            noteType,
          },
        }
      },
      invalidatesTags: ['videos'],
    }),
    bindSimulatorSlug: build.mutation({
      query: ({ videoId, simulatorSlug }: { videoId: string; simulatorSlug: string }) => {
        return {
          method: 'PUT',
          url: `admin/videos/${videoId}/simulators/${simulatorSlug}`,
        }
      },
      invalidatesTags: ['videos'],
    }),
    unbindSimulatorSlug: build.mutation({
      query: ({ videoId, simulatorSlug }: { videoId: string; simulatorSlug: string }) => {
        return {
          method: 'DELETE',
          url: `admin/videos/${videoId}/simulators/${simulatorSlug}`,
        }
      },
      invalidatesTags: ['videos'],
    }),
  }),
  reducerPath: 'videosService',
  refetchOnMountOrArgChange: true,
  tagTypes: ['videos', 'delete comment-card'],
})

export const {
  useAddDaysForVideoSettingsMutation,
  useAddVideoMutation,
  useChangeVideosOrderMutation,
  useDeleteVideoMutation,
  useGetMDXContentQuery,
  useGetMDXFilesTreeQuery,
  useGetVideoByIdQuery,
  useGetVideosQuery,
  useUpdateVideoMutation,
  useAddMDXNoteMutation,
  useUpdateMainMDXNoteMutation,
  useUpdatePreviewMDXNoteMutation,
  useDeleteMDXNoteMutation,
  useBindSimulatorSlugMutation,
  useUnbindSimulatorSlugMutation,
  util,
  useUpdateVideoImagesMutation,
} = videosService
